import { useState, useEffect } from 'react'
import { AxiosError } from 'axios'
import { Flex } from '@radix-ui/themes'
import { useLoaderData, useNavigate } from 'react-router'
import { formatTime } from '@/lib/shipment'
import { createClient } from '@/api-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import { PageHeading } from '~/components/subscription/page-heading'
import { FormLabel } from '~/components/subscription/form-label'
import { Button } from '~/components/subscription/button'
import { TextLink } from '@/components/subscription/text-link'
import { styles } from './styles.css'

export const loader = async () => {
  const subscription = await fetchSubscription()
  const address = await fetchAddress(subscription).catch(() => ({ data: { data: { attributes: {} } } } as Awaited<ReturnType<typeof fetchAddress>>))

  return [subscription, address] as const
}

async function fetchSubscription() {
  const subscriptions = await createClient(Subscriptions).getSubscriptions()

  return subscriptions.data.data[0]
}

function fetchAddress(subscription) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdAddress(subscription.id)
}

function updateSubscriptionAddress(subscription, address) {
  const params = {
    kind: address.with_sender_name ? 'gift' : 'home',
    sender_name: address.with_sender_name ? address.sender_name : '',
    postal_code: address.postal_code,
    state: address.state,
    city_and_street: address.city_and_street,
    others: address.others,
    name: address.name,
    phone_number: address.phone_number,
  }

  return createClient(Subscriptions).putSubscriptionsSubscriptionIdAddress(subscription.id, { ...params })
}

export const SubscriptionMypageAddress: React.FC = () => {
  const [
    subscription,
    {
      data: { data: registeredAddress },
    },
  ] = useLoaderData<typeof loader>()

  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [streetError, setStreetError] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [address, setAddress] = useState(() => {
    if (registeredAddress) {
      return {
        ...registeredAddress.attributes,
        with_sender_name: !!registeredAddress.attributes.sender_name,
      }
    } else {
      return {
        name: '',
        postal_code: '',
        state: '',
        city_and_street: '',
        others: '',
        phone_number: '',
        with_sender_name: false,
        sender_name: '',
      }
    }
  })

  useEffect(() => {
    const { postal_code } = address

    if (postal_code.length < 7) return

    if (!address.city_and_street) {
      const code = postal_code.replace('-', '')
      window
        .fetch(`https://joe-noh.github.io/jp-postal-code-api/api/v1/${code}.json`)
        .then((res) => res.json())
        .then((json) => {
          const addr = json['addresses'][0]['ja']

          if (addr) {
            setAddress((prev) => ({
              ...prev,
              state: addr['prefecture'],
              city_and_street: addr['address1'] + addr['address2'],
            }))
          }
        })
        .catch(() => {}) // 補完に失敗しても無視
    }
  }, [address.postal_code])

  const handleChangeAddress = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    setAddress((prev) => ({ ...prev, [name]: value }))
  }

  const validateCityAndStreet = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.value.match(/\d/)) {
      setStreetError('番地が記載されていない可能性があります。ご確認ください')
    } else {
      setStreetError('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isSubmitting) {
      setIsSubmitting(true)

      try {
        await updateSubscriptionAddress(subscription, address)

        navigate('/subscription/mypage', { state: { message: 'お届け先を変更しました' } })
      } catch (error) {
        if (error instanceof AxiosError) {
          setErrorMessage(error.response.data.error.message_displayable || '入力内容をご確認ください')
        }
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Flex direction="column" align="center" gap="32px" width="100%">
        <PageHeading>お届け先変更</PageHeading>
        {subscription && (
          <p className={styles.warn}>
            {formatTime(1000 * subscription.attributes.skip_deadline)}までに変更すると、
            <br />
            次回分の注文に反映されます
          </p>
        )}
        <Flex direction="column" align="start" gap="8px" width="100%">
          <Flex direction="column" align="start" gap="4px" width="100%">
            <FormLabel htmlFor="name">お名前</FormLabel>
            <input type="text" id="name" name="name" required className={styles.input} value={address.name} onChange={handleChangeAddress} />
          </Flex>
          <Flex direction="column" align="start" gap="4px" width="100%">
            <FormLabel htmlFor="postal-code">郵便番号</FormLabel>
            <input
              type="text"
              id="postal-code"
              name="postal_code"
              required
              className={styles.input}
              value={address.postal_code}
              onChange={handleChangeAddress}
            />
          </Flex>
          <Flex direction="column" align="start" gap="8px" width="100%">
            <Flex direction="column" align="start" gap="4px" width="100%">
              <FormLabel htmlFor="state">都道府県</FormLabel>
              <select id="state" name="state" required className={styles.input} value={address.state} onChange={handleChangeAddress}>
                <option value="北海道">北海道</option>
                <option value="青森県">青森県</option>
                <option value="岩手県">岩手県</option>
                <option value="宮城県">宮城県</option>
                <option value="秋田県">秋田県</option>
                <option value="山形県">山形県</option>
                <option value="福島県">福島県</option>
                <option value="茨城県">茨城県</option>
                <option value="栃木県">栃木県</option>
                <option value="群馬県">群馬県</option>
                <option value="埼玉県">埼玉県</option>
                <option value="千葉県">千葉県</option>
                <option value="東京都">東京都</option>
                <option value="神奈川県">神奈川県</option>
                <option value="新潟県">新潟県</option>
                <option value="富山県">富山県</option>
                <option value="石川県">石川県</option>
                <option value="福井県">福井県</option>
                <option value="山梨県">山梨県</option>
                <option value="長野県">長野県</option>
                <option value="岐阜県">岐阜県</option>
                <option value="静岡県">静岡県</option>
                <option value="愛知県">愛知県</option>
                <option value="三重県">三重県</option>
                <option value="滋賀県">滋賀県</option>
                <option value="京都府">京都府</option>
                <option value="大阪府">大阪府</option>
                <option value="兵庫県">兵庫県</option>
                <option value="奈良県">奈良県</option>
                <option value="和歌山県">和歌山県</option>
                <option value="鳥取県">鳥取県</option>
                <option value="島根県">島根県</option>
                <option value="岡山県">岡山県</option>
                <option value="広島県">広島県</option>
                <option value="山口県">山口県</option>
                <option value="徳島県">徳島県</option>
                <option value="香川県">香川県</option>
                <option value="愛媛県">愛媛県</option>
                <option value="高知県">高知県</option>
                <option value="福岡県">福岡県</option>
                <option value="佐賀県">佐賀県</option>
                <option value="長崎県">長崎県</option>
                <option value="熊本県">熊本県</option>
                <option value="大分県">大分県</option>
                <option value="宮崎県">宮崎県</option>
                <option value="鹿児島県">鹿児島県</option>
                <option value="沖縄県" disabled>
                  沖縄県
                </option>
              </select>
            </Flex>
            <Flex direction="column" align="start" gap="4px" width="100%">
              <FormLabel htmlFor="city-and-street">市区町村・番地</FormLabel>
              <input
                type="text"
                id="city-and-street"
                name="city_and_street"
                required
                className={styles.input}
                value={address.city_and_street}
                onChange={handleChangeAddress}
                onBlur={validateCityAndStreet}
              />
              {streetError && <p className={styles.inputError}>{streetError}</p>}
            </Flex>
            <Flex direction="column" align="start" gap="4px" width="100%">
              <FormLabel htmlFor="others">建物名・部屋番号</FormLabel>
              <input type="text" id="others" name="others" className={styles.input} value={address.others} onChange={handleChangeAddress} />
            </Flex>
            <Flex direction="column" align="start" gap="4px" width="100%">
              <FormLabel htmlFor="phone-number">電話番号</FormLabel>
              <input
                type="text"
                id="phone-number"
                name="phone_number"
                required
                className={styles.input}
                value={address.phone_number}
                onChange={handleChangeAddress}
              />
            </Flex>
            <label className={styles.checkbox}>
              <Flex direction="row" align="center" gap="4px">
                <input
                  type="checkbox"
                  name="with_sender_name"
                  checked={address.with_sender_name}
                  onChange={(e) => setAddress((prev) => ({ ...prev, with_sender_name: e.target.checked }))}
                />
                <span>注文者と受取人が異なる</span>
              </Flex>
            </label>
            {address.with_sender_name && (
              <Flex direction="column" align="start" gap="8px" width="100%">
                <Flex direction="column" align="start" gap="4px" width="100%">
                  <FormLabel htmlFor="sender-name">ご注文者様のお名前</FormLabel>
                  <input
                    type="text"
                    id="sender-name"
                    name="sender_name"
                    required={address.with_sender_name}
                    className={styles.input}
                    value={address.sender_name}
                    onChange={handleChangeAddress}
                  />
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>
        <Flex direction="column" align="center" gap="32px" width="100%">
          <Flex direction="column" align="center" gap="16px" width="100%">
            <Button as="button" type="submit" size="m" fluid={true} disabled={isSubmitting}>
              お届け先を変更する
            </Button>
            {errorMessage && <p className={styles.error}>{errorMessage}</p>}
          </Flex>
          <TextLink to="/subscription/mypage">マイページに戻る</TextLink>
        </Flex>
      </Flex>
    </form>
  )
}
